import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { X, Plus, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { allCategories, catColor, catLabel } from "@/lib/lore";
import { useTimelines, useInvalidate } from "@/lib/queries";
import { AttributeEditor } from "@/components/AttributeEditor";
import { TargetPicker, MergeModePanel } from "@/components/MergeTargetPanel";
import { undoToast } from "@/lib/undo";

const Row = ({ i, label, children }) => (
  <motion.div initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.12, duration: 0.3 }} className="py-4 border-b border-[#2D3442] last:border-0">
    <div className="lf-label mb-2.5">{label}</div>
    {children}
  </motion.div>
);

const CatSelect = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-8 w-40 text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{allCategories().map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
  </Select>
);

function RelatedList({ related, stubs, setStubs, links, setLinks }) {
  if (!related.length) return <p className="text-sm text-[#A8A090]">No other entries mentioned.</p>;
  return (
    <div className="space-y-2" data-testid="extraction-related">
      {related.map((r) => (
        <div key={r.name} className="flex items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2">
            {r.existing_id ? <Link2 className="h-3.5 w-3.5 text-[#10B981]" /> : <Plus className="h-3.5 w-3.5 text-[#A8A090]" />}
            <span className="text-[#F3EBDD]">{r.name}</span>
            {r.existing_id && <span className="text-xs" style={{ color: catColor(r.category) }}>linked · {catLabel(r.category)}</span>}
          </div>
          {!r.existing_id && links[r.name] && (
            <label className="flex items-center gap-1.5 text-xs text-[#F59E0B]">
              <Checkbox checked={links[r.name].on} onCheckedChange={(v) => setLinks({ ...links, [r.name]: { ...links[r.name], on: !!v } })} data-testid={`related-link-${r.name}`} />
              same as {r.candidates[0].name}?
            </label>
          )}
          {!r.existing_id && !links[r.name]?.on && (
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-[#A8A090]">
                <Checkbox checked={stubs[r.name].create} onCheckedChange={(v) => setStubs({ ...stubs, [r.name]: { ...stubs[r.name], create: !!v } })} data-testid={`stub-create-${r.name}`} />
                create stub
              </label>
              <CatSelect value={stubs[r.name].category} onChange={(v) => setStubs({ ...stubs, [r.name]: { ...stubs[r.name], category: v } })} testid={`stub-category-${r.name}`} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function EventRefs({ refs, onChange }) {
  const upd = (i, k, v) => onChange(refs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  if (!refs.length) return <p className="text-sm text-[#A8A090]">No dated events found.</p>;
  return (
    <div className="space-y-2" data-testid="extraction-events">
      {refs.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_80px_32px] gap-2 sm:grid-cols-[1fr_140px_90px_32px]">
          <Input className="col-span-3 h-9 sm:col-span-1" value={r.event_name} onChange={(e) => upd(i, "event_name", e.target.value)} data-testid={`extraction-event-name-${i}`} />
          <Input className="h-9" value={r.display_date} onChange={(e) => upd(i, "display_date", e.target.value)} placeholder="Display date" data-testid={`extraction-event-date-${i}`} />
          <Input className="h-9 font-mono-lf" type="number" value={r.timestamp_int} onChange={(e) => upd(i, "timestamp_int", parseInt(e.target.value || "0", 10))} data-testid={`extraction-event-ts-${i}`} />
          <Button size="icon" variant="ghost" className="h-9 w-8" onClick={() => onChange(refs.filter((_, j) => j !== i))} data-testid={`extraction-event-remove-${i}`}><X className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
}

export const ExtractionPreview = ({ extraction, noteText, uid, onDone, onCancel }) => {
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const feats = useQueryClient().getQueryData(["universe", uid])?.features;
  const isPro = !feats || feats.includes("timelines");
  const { data: timelines = [] } = useTimelines(uid, isPro);
  const [d, setD] = useState(extraction);
  const [timelineId, setTimelineId] = useState("");
  const [stubs, setStubs] = useState(Object.fromEntries(extraction.related_matches.filter((r) => !r.existing_id).map((r) => [r.name, { create: true, category: "Lore" }])));
  const [links, setLinks] = useState(Object.fromEntries(extraction.related_matches.filter((r) => !r.existing_id && r.candidates?.length).map((r) => [r.name, { id: r.candidates[0].id, on: r.candidates[0].score >= 85 }])));
  const [targetId, setTargetId] = useState(extraction.match_candidates?.[0]?.score >= 90 ? extraction.match_candidates[0].id : null);
  const [addAlias, setAddAlias] = useState(true);
  const [mode, setMode] = useState("append");
  const [woven, setWoven] = useState(null);
  const mergeInto = extraction.existing_match?.id || targetId;
  const [busy, setBusy] = useState(false);
  const tid = timelineId || timelines[0]?.id || "";
  const set = (k) => (v) => setD({ ...d, [k]: v?.target ? v.target.value : v });

  const save = async () => {
    setBusy(true);
    try {
      const linked = Object.fromEntries(Object.entries(links).filter(([, l]) => l.on).map(([n, l]) => [n, l.id]));
      const create = Object.entries(stubs).filter(([n, s]) => s.create && !linked[n]).map(([n]) => n);
      const weave = mergeInto && mode === "weave" && woven;
      const { data } = await api.post(`/universes/${uid}/ai/commit`, {
        note_text: noteText, entity_type: d.entity_type, name: d.name, summary: d.summary,
        extracted_attributes: d.extracted_attributes, timeline_references: d.timeline_references,
        related_entity_names: d.related_matches.map((r) => r.name), create_stub_names: create, aliases: (d.aliases || []).filter((a) => a.trim()),
        stub_categories: Object.fromEntries(Object.entries(stubs).map(([n, s]) => [n, s.category])), timeline_id: tid || null,
        target_entity_id: targetId, add_alias: addAlias, related_links: linked, merge_mode: weave ? "weave" : "append",
        ...(weave ? { woven_summary: woven.summary, woven_content: woven.content, extracted_attributes: woven.attributes } : {}),
      });
      invalidate(["entities", uid], ["timelines", uid], "events", ["entity", data.entity_id], ["languages", uid]);
      setTimeout(() => invalidate(["entities", uid], ["entity", data.entity_id], ["suggestions", uid]), 30000);
      const desc = `${data.stubs_created.length} stubs · ${data.events_created} events · continuity check running`;
      if (data.merged) undoToast({ id: data.entity_id, universe_id: uid, name: d.name }, `Added to ${d.name}`, invalidate, { description: `${desc} · Undo reverts the entry text` });
      else toast.success(`Created ${d.name}`, {
        description: desc,
        action: data.language_id
          ? { label: "Open language", onClick: () => navigate(`/u/${uid}/languages/${data.language_id}`) }
          : { label: "Open", onClick: () => navigate(`/u/${uid}/lore/${data.entity_id}`) },
      });
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-[#D97706]/30 bg-[#13171F] p-6" data-testid="extraction-preview">
      <div className="lf-label text-[#F59E0B] mb-2">Extraction · review before saving</div>
      {extraction.ai_failed && (
        <p className="mb-3 rounded border border-[#F59E0B]/40 bg-[#F59E0B]/5 px-3 py-2 text-sm text-[#E8DFD1]" data-testid="extraction-ai-failed">
          The scribe couldn't analyse this note automatically, so here is a simple draft. Adjust the name, category and details, then save. Your full note text is kept.
        </p>
      )}
      <Row i={0} label="Existing entry">
        <TargetPicker extraction={extraction} targetId={targetId} setTargetId={(v) => { setTargetId(v); setWoven(null); }} addAlias={addAlias} setAddAlias={setAddAlias} name={d.name} />
        {mergeInto && <div className="mt-3"><MergeModePanel targetId={mergeInto} noteText={noteText} d={d} mode={mode} setMode={setMode} woven={woven} setWoven={setWoven} /></div>}
      </Row>
      <Row i={0} label="Entry">
        <div className="flex gap-2">
          <Input className="font-display text-2xl h-11" value={d.name} onChange={set("name")} data-testid="extraction-name" />
          <Select value={d.entity_type} onValueChange={set("entity_type")}>
            <SelectTrigger className="w-48 h-11" data-testid="extraction-type"><SelectValue /></SelectTrigger>
            <SelectContent>{allCategories().map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Row>
      <Row i={1} label="Also known as">
        <Input value={(d.aliases || []).join(", ")} onChange={(e) => set("aliases")(e.target.value.split(",").map((a) => a.trimStart()))} placeholder="Other names, titles, spellings" data-testid="extraction-aliases" />
      </Row>
      <Row i={1} label="Summary"><Textarea rows={3} value={d.summary} onChange={set("summary")} data-testid="extraction-summary" /></Row>
      <Row i={2} label="Attributes"><AttributeEditor value={d.extracted_attributes} onChange={set("extracted_attributes")} testid="extraction-attr" /></Row>
      {isPro && <Row i={3} label="Timeline events">
        <EventRefs refs={d.timeline_references} onChange={set("timeline_references")} />
        {d.timeline_references.length > 0 && (
          <div className="mt-3 flex items-center gap-2 text-sm text-[#A8A090]">Add to
            <Select value={tid} onValueChange={setTimelineId}>
              <SelectTrigger className="h-8 w-56" data-testid="extraction-timeline-select"><SelectValue placeholder="Choose timeline" /></SelectTrigger>
              <SelectContent>{timelines.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </Row>}
      <Row i={4} label="Mentioned entries"><RelatedList related={d.related_matches} stubs={stubs} setStubs={setStubs} links={links} setLinks={setLinks} /></Row>
      <div className="sticky bottom-0 z-10 -mx-4 flex justify-end gap-2 border-t border-[#2D3442] bg-[#0F131A] px-4 py-3 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:pt-5 sm:pb-0">
        <Button variant="ghost" onClick={onCancel} data-testid="extraction-cancel-button">Discard</Button>
        <Button onClick={save} disabled={busy || !d.name.trim() || (mergeInto && mode === "weave" && !woven)} data-testid="extraction-save-button">{busy ? "Saving…" : mergeInto ? (mode === "weave" ? "Save woven entry" : "Add to entry & save") : "Save to Lore Bible"}</Button>
      </div>
    </div>
  );
};
